// Plain-language text for the Options trader screens. The engine's codes stay exactly as they are
// (they are shown, unchanged, under Details / Advanced); this only says the same thing in words.

const LEG = { CE: "the call (CE)", PE: "the put (PE)" };

// one code (no CE_/PE_ prefix) -> sentence; `side` fills in which leg
const REASONS = {
  MIN_EXPIRY_PROFIT_NOT_MET: () => "no pair reaches the trade's Minimum Expiry Profit right now - waiting (no lower pair is taken)",
  NO_TICK: (s) => `no live price for ${s} yet`,
  DEPTH_STALE: (s) => `${s} order book is not updating`,
  WAITING_FOR_QUOTE: (s) => `waiting for a confirmed quote for ${s} (the live connection has gone quiet)`,
  OFFER_MISSING: (s) => `nobody is selling ${s} right now`,
  OFFER_QTY_BELOW_LOT: (s) => `less than one lot is on offer for ${s}`,
  SPREAD_UNKNOWN: (s) => `${s} has no buyers, so its spread is unknown`,
  BOOK_CROSSED: (s) => `${s} order book is crossed`,
  SPREAD_TOO_WIDE: (s) => `${s} bid-offer spread is too wide`,
  OI_MISSING: (s) => `open interest for ${s} is unknown`,
  OI_ZERO: (s) => `${s} has no open interest`,
  OI_BELOW_MIN: (s) => `open interest of ${s} is below your minimum`,
  LTP_OLD_TRADE: (s, c) => (c?.mode === "LTP" ? `${s} last traded a while ago (still sent at that LTP; the live offer decides the fill)`
    : `${s} last traded a while ago (the order is priced on the live offer)`),
  NO_VOLUME_TODAY: (s, c) => (c?.mode === "LTP" ? `${s} has not traded today (still sent at its LTP; the live offer decides the fill)`
    : `${s} has not traded today (the order is priced on the live offer)`),
  LTP_UNUSABLE: (s) => `${s} has no LTP to send it at`,
  WAITING_AT_LTP: (s) => `${s} is waiting at its LTP limit`,
  MARKET_NOT_ENABLED: () => "this market is not enabled for Options Hedging",
  SELL_LIVE_NOT_ENABLED: () => "LIVE SELL OPTIONS is not authorized for this Zerodha account — ask an admin",
  NOT_A_CE_PE_PAIR: () => "the two contracts are not a call/put pair",
  CONTRACT_MISMATCH: () => "the two contracts do not share an underlying and expiry",
  LOT_MISMATCH: () => "the two contracts have different lot sizes",
  STRIKE_ORDER: () => "the call strike must be below the put strike",
  EXPIRED: () => "the contract has expired",
  EXPIRY_DAY: () => "no new trades on expiry day",
  DTE_BELOW_MIN: () => "below your saved minimum days to expiry (Options Hedging settings)",
  EXIT_WITHIN_HOLD_WINDOW: () => "the mandatory pre-expiry exit is too close for a new trade (minimum holding window)",
  EXIT_SCHEDULE_UNKNOWN: () => "the pre-expiry exit time cannot be worked out (trading calendar)",
  UNDERLYING_MISSING: () => "no live price for the underlying",
  UNDERLYING_STALE: () => "the underlying's price is not updating",
  NOT_ITM: () => "the strikes are no longer in the money",
  OPPORTUNITY_UNKNOWN: () => "the opportunity cannot be priced yet",
  OPPORTUNITY_BELOW_MIN: () => "waiting for the opportunity at the offers to reach your Spread to Maintain",
  MARKET_CLOSED: () => "the market is closed",
  EXCHANGE_HOLIDAY: () => "the exchange is closed today (holiday)",
  OUTSIDE_TRADING_HOURS: () => "outside trading hours",
  BROKER_DISCONNECTED: () => "Zerodha is not connected",
  NO_BROKER_SESSION: () => "Zerodha is not connected",
  ACCOUNT_NOT_SHADOW_ENABLED: () => "SHADOW trading is not enabled for your account",
  ACCOUNT_NOT_LIVE_ENABLED: () => "LIVE trading is not enabled for your account",
  KILL_SWITCH: () => "the kill switch is on",
  STOP_NEW_ENTRIES: () => "new entries are stopped (Broker Session → risk controls)",
  RECON_NOT_OK: () => "account reconciliation needs attention",
  CONTRACT_NOT_FOUND: () => "the contract is not in today's instrument list",
  CONTRACT_NOT_CURRENT: () => "the contract is no longer listed by the exchange today (delisted or re-listed)",
  CONTRACT_NOT_IN_MASTER: () => "not in the trading server's current instrument list - a trade would be refused",
  // sizing / capital
  REQUESTED_PREMIUM_UNKNOWN: () => "the premium cannot be priced yet",
  BROKER_CAPITAL_UNAVAILABLE: () => "Zerodha's margin calculator did not answer",
  ENTRY_CHARGES_UNAVAILABLE: () => "entry charges are not available from Zerodha yet",
  EXIT_CHARGES_UNAVAILABLE: () => "exit charges are not available from Zerodha yet",
  MULTIPLIER_PENDING: () => "the contract size has not been measured yet",
  CAPITAL_PER_PAIR_UNKNOWN: () => "the capital for one pair is unknown",
  ALLOCATED_FUND_REQUIRED: () => "enter the amount to allocate",
  ALLOCATED_FUND_BELOW_ONE_PAIR: () => "the allocated amount is less than one pair needs",
  OWNER_FUNDS_UNAVAILABLE: () => "your Options capital pool does not have enough room (Settings)",
  TRADE_FUNDS_EXHAUSTED: () => "the trade's allocated fund is used up",
  FUNDS_STALE: () => "Zerodha's funds figure is out of date",
  AVAILABLE_FUNDS_UNKNOWN: () => "Zerodha's available funds are unknown",
  INSUFFICIENT_BROKER_FUNDS: () => "not enough funds in the Zerodha account",
  // start / account caps / engine
  OPTIONS_NOT_PERMITTED: () => "this account is not set up for Options trading",
  FUTURES_NOT_PERMITTED: () => "this account is not set up for Futures trading",
  OPTIONS_MAX_OPEN_TRADES_REACHED: () => "you already have the maximum number of open options trades",
  OPTIONS_MAX_OPEN_TRADES_NOT_CONFIGURED: () => "the maximum number of options trades is not set (admin)",
  OPTIONS_DAILY_LOSS_LIMIT_REACHED: () => "today's options loss limit has been reached",
  OPTIONS_DAILY_LOSS_LIMIT_NOT_CONFIGURED: () => "a daily loss limit must be set by an admin before LIVE trading",
  LIVE_CAPITAL_REFUSED: () => "the account's LIVE capital limits do not allow this trade",
  BROKER_WRITES_DISABLED: () => "order sending is switched off on this server",
  // backend startup: the Zerodha instrument list is still loading (no quotes, band checks or exits yet)
  INSTRUMENTS_SYNCING: () => "waiting for Zerodha's instrument list after a restart - checks resume when it arrives",
  INSTRUMENTS_NOT_READY: () => "Zerodha's instrument list has not loaded for over a minute - check Broker Session; exits cannot be evaluated",
  NOT_SIZABLE: () => "the trade cannot be sized",
  NOT_STARTABLE: () => "the trade cannot start right now",
  // both legs entered together; one leg short -> 3-minute recovery -> failed-pair unwind
  ENTRY_RETRIES_EXHAUSTED: () => "neither leg filled after the allowed retries",
  FAILED_PAIRS_LIMIT: () => "too many pairs failed to complete (each was sold at its cost-recovery price)",
  FAILED_PAIR_UNWIND: () => "selling the filled leg of a failed pair at its cost-recovery price",
  RECOVERY_PRICE_NOT_REACHED: () => "the failed pair's leg did not sell at its cost-recovery price by the park time",
  OFFER_ABOVE_CAP: () => "the missing leg's offer is above the price that keeps your minimum opportunity",
  NO_OFFER: () => "nobody is selling the missing leg right now",
  REFUSED: () => "the last attempt was refused; trying again",
};

/** "CE_OI_BELOW_MIN" / "OPTIONS_MAX_OPEN_TRADES_REACHED(2>1)" -> a readable sentence. */
const n2 = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));

/** The engine's WAIT reasons (eligibility.WAIT_REASONS): strategy / quote conditions the trade waits
 *  for. Everything else is a hard safety block. */
const WAIT = ["OPPORTUNITY_BELOW_MIN", "OPPORTUNITY_UNKNOWN", "UNDERLYING_STALE", "UNDERLYING_MISSING", "NOT_ITM"];
const WAIT_LEG = ["WAITING_FOR_QUOTE", "NO_TICK", "OFFER_MISSING", "OFFER_QTY_BELOW_LOT", "SPREAD_UNKNOWN", "SPREAD_TOO_WIDE", "BOOK_CROSSED", "LTP_UNUSABLE"];
export const isWaitReason = (c) => WAIT.includes(c) || (/^(CE|PE)_/.test(c || "") && WAIT_LEG.includes(String(c).slice(3)));

/** "WAITING FOR OPPORTUNITY · CE 110.00 · PE needs ≤ 128.00 (now 129.00) · target 2.00 (now 1.00)" */
export function waitingLine(w, reasons = []) {
  // the ACTUAL blocker first: any price / quote problem (no tick, stale or unusable LTP, missing offer, ...) is
  // what the trade waits for - only when nothing but the opportunity is short does it wait for opportunity
  const mode = (w && w.priced_on) || "OFFER";
  const price = reasons.filter((r) => !/^OPPORTUNITY_/.test(r));
  if (price.length || !w) {
    return `WAITING FOR PRICE — ${price.map((r) => explain(r, { mode })).join("; ") || "no executable quote yet"}`;
  }
  const label = mode === "LTP" ? "LTP" : "offer";
  return `WAITING FOR OPPORTUNITY · CE ${label} ${n2(w.ce_offer)} · PE needs ≤ ${n2(w.required_pe_max)} (now ${n2(w.pe_offer)})`
    + ` · or CE ≤ ${n2(w.required_ce_max)} at this PE · target ${n2(w.target_opportunity)} (now ${n2(w.current_opportunity)})`;
}

export function explain(code, ctx) {
  if (!code) return "";
  const raw = String(code);
  if (raw.startsWith("ORDER_REFUSED:")) {
    // SELL entry block (backend sell_machine BLOCKING_REFUSALS): refused before the broker for a reason every
    // new pair would hit too - no new pair until the backoff ends
    const what = raw.slice("ORDER_REFUSED:".length);
    return `orders refused before the broker (${what === "INSTRUMENT_NOT_ALLOWLISTED" ? "this instrument is not enabled for the account"
      : what.toLowerCase().replace(/_/g, " ")}) - no new pair until it is fixed; re-checked with backoff`;
  }
  const base = raw.split("(")[0].split(":")[0].trim().replace(/^FAILED_PAIR_UNWIND_P\d+$/, "FAILED_PAIR_UNWIND");
  const m = /^(CE|PE)_(.+)$/.exec(base);
  if (m && REASONS[m[2]]) return REASONS[m[2]](LEG[m[1]], ctx);
  if (REASONS[base]) return REASONS[base]();
  return base.toLowerCase().replace(/_/g, " ");
}

/** The first reason in words, plus how many more there are. */
export function explainFirst(codes) {
  const list = (codes || []).filter(Boolean);
  if (!list.length) return "";
  const first = explain(list[0]);
  const text = first.charAt(0).toUpperCase() + first.slice(1);
  return list.length > 1 ? `${text} (and ${list.length - 1} more)` : text;
}

const STATUS = {
  DRAFT: "Ready to start", READY: "Ready to start", HOLDING: "Holding", EXITING: "Squaring off", COMPLETE: "Completed",
  STOPPED: "Stopped", ENTRY_STOPPED_FUNDS: "Holding (entry stopped: funds)", ENTRY_STOPPED_WINDOW: "Holding (entry window over)",
  ENTRY_STOPPED_UNHEDGED: "Needs attention", ENTRY_STOPPED_LEG1: "Entry stopped",
};

/** A trade's status as a short phrase (the original status code stays in Details). */
export function tradeStatusText(t) {
  const held = t.entered_pairs || 0;
  const planned = t.planned_pairs || 0;
  if (t.recovery_state && !["ACKNOWLEDGED", null, undefined].includes(t.recovery_state)) return "Waiting for your acknowledgement";
  if (t.status === "ENTERING") {
    if ((t.waiting_reasons || []).length) return `Waiting — ${explain(t.waiting_reasons[0])}`;
    return `Entering pair ${Math.min(held + 1, planned || held + 1)} of ${planned || "?"}`;
  }
  if (t.status === "HOLDING" || (t.status || "").startsWith("ENTRY_STOPPED_") && t.status !== "ENTRY_STOPPED_UNHEDGED" && held) {
    return `Holding ${held} pair${held === 1 ? "" : "s"}`;
  }
  if (t.status === "EXITING" && (t.waiting_reasons || []).length) return `Squaring off — waiting: ${explain(t.waiting_reasons[0])}`;
  if (t.status === "COMPLETE" && t.realized_pnl?.net !== undefined && t.realized_pnl?.net !== null) {
    const v = Number(t.realized_pnl.net);
    return `Completed (${v >= 0 ? "+" : "−"}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })})`;
  }
  if (t.status === "STOPPED") return t.stopped_reason ? `Stopped — ${explain(t.stopped_reason)}` : "Stopped (nothing filled)";
  return STATUS[t.status] || t.status;
}

export const STATUS_TONE = (t) => {
  if (t.status === "ENTRY_STOPPED_UNHEDGED" || (t.recovery_state && t.recovery_state !== "ACKNOWLEDGED")) return "bad";
  if (t.status === "ENTERING" || t.status === "EXITING") return "busy";
  if (t.status === "COMPLETE" || t.status === "STOPPED") return "done";
  if (t.status === "DRAFT" || t.status === "READY") return "idle";
  return "good";
};

/** A pair whose second leg is still being completed (the 3-minute recovery window), in words. */
export function pairRecovering(p, now = Date.now() / 1000) {
  const held = p.leg_order?.L1, missing = p.leg_order?.L2;
  const left = Math.max(0, Math.round((p.recover_until || 0) - now));
  const w = p.recovery?.waiting ? ` — waiting: ${p.recovery.waiting.split(",").map(explain).join("; ")}` : "";
  return `Pair ${p.pair_no}: ${held} filled, completing ${missing} (up to ₹${p.cap_l2}, ${left}s left)${w}`;
}

/** LTP mode: one leg filled, the other resting at its original LTP (never cancelled or re-priced). */
export function pairLegWait(p) {
  const held = p.leg_order?.L1, missing = p.leg_order?.L2;
  const lim = (p.wait_limit || {})[missing];
  const since = p.wait_since ? new Date(p.wait_since * 1000).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "";
  const closed = p.wait_waiting === "MARKET_CLOSED" ? " · market closed, re-placed when it opens" : "";
  return `Pair ${p.pair_no}: ${held} filled @ ${p.l1_avg ?? "—"} — ${missing} waiting at ${lim ?? "—"}${since ? ` since ${since}` : ""} (kept until filled; re-placed each session)${closed}`;
}

/** A pair needing a human, in words. */
export function pairProblem(p) {
  const name = `Pair ${p.pair_no}`;
  if (p.state === "UNHEDGED") return `${name} holds only one leg — the hedge is incomplete.`;
  if (p.state === "UNHEDGED_RESIDUAL") return `${name} holds unequal quantities on its two legs.`;
  if (p.state === "AMBIGUOUS") return `${name}: waiting for Zerodha to confirm an order.`;
  if (p.state === "EXIT_AMBIGUOUS") return `${name}: waiting for Zerodha to confirm an exit order.`;
  if (p.state === "EXIT_ESCALATED" && p.exit?.escalated === "RECOVERY_PRICE_NOT_REACHED") {
    const tg = Object.entries(p.exit?.recovery?.targets || {}).map(([s, v]) => `${s} at ₹${v.price}`).join(", ");
    return `${name} could not be completed, and its filled leg did not sell at its cost-recovery price${tg ? ` (${tg})` : ""} by the park time. The order is still resting.`;
  }
  if (p.state === "EXIT_ESCALATED") return `${name}: the exit is not filling.`;
  return `${name}: ${p.state}`;
}

// readiness check -> what a trader should do about it (only failing checks are shown to traders)
const FIX = {
  broker_connected: ["Zerodha is not connected.", "/broker", "Connect Zerodha"],
  account_linked: ["No Zerodha account is linked.", "/broker", "Connect Zerodha"],
  options_pool_limit_set: ["Your Options capital pool is not set or is full.", "settings", "Open Settings"],
  options_instrument_access: ["No Options instruments are allowed on this account — ask an admin.", null, null],
  options_order_size_limits: ["The account's Options size limits are not set — ask an admin.", null, null],
  account_options_hedge_enabled: ["Options Hedging is not enabled for this account — ask an admin.", null, null],
  account_options_shadow_enabled: ["SHADOW trading is not enabled for this account — ask an admin.", null, null],
  account_options_live_enabled: ["LIVE trading is not enabled for this account — ask an admin.", null, null],
  no_kill_switch: ["The kill switch or Stop new entries is on.", "/broker", "Risk controls (Broker Session)"],
  reconciliation_ok: ["Account reconciliation needs attention.", null, null],
  no_recovery_pending: ["A trade is waiting for your acknowledgement after a restart.", null, null],
  shadow_not_halted: ["SHADOW trading is halted — check Zerodha, then clear it on SHADOW LIVE.", "/shadow-live", "SHADOW LIVE"],
  options_max_open_trades: ["You already have the maximum number of open options trades.", null, null],
  options_daily_loss_limit: ["Today's loss limit is reached (or not set for LIVE).", null, null],
  exchange_holiday_calendar: ["The holiday calendar needs refreshing — ask an admin.", null, null],
  shadow_account_unfunded: ["The SHADOW account holds money — it must be (nearly) empty.", null, null],
  shadow_account_no_collateral: ["The SHADOW account holds collateral — it must have none.", null, null],
  broker_no_positions: ["The SHADOW account has open positions at Zerodha.", null, null],
  broker_no_open_orders: ["The SHADOW account has open orders at Zerodha.", null, null],
  live_account_funded: ["The LIVE account shows no available funds.", null, null],
  live_capital_limits: ["The account's LIVE capital limits are not set — ask an admin.", null, null],
};

export function readinessProblem(check) {
  const f = FIX[check.check];
  return f ? { text: f[0], link: f[1], linkLabel: f[2] } : { text: `${check.check.replace(/_/g, " ")} needs attention.`, link: null };
}

// SELL strike window (backend option_sell_math.window_strikes): strikes ITM / OTM of the ATM, per type.
// Scanner Settings: all four or none (blank = the symmetric Max ITM window on both sides). New SELL trades
// keep the values; every pair and replacement leg stays inside them.
export const SELL_WINDOW = [
  ["sell_ce_itm_strikes", "CE ITM strikes"], ["sell_ce_otm_strikes", "CE OTM strikes"],
  ["sell_pe_itm_strikes", "PE ITM strikes"], ["sell_pe_otm_strikes", "PE OTM strikes"],
];
export const windowText = (w) => (w ? `CE ${w.ce_itm} ITM / ${w.ce_otm} OTM · PE ${w.pe_itm} ITM / ${w.pe_otm} OTM · + ATM` : null);

// Scanner Margin column (the worker's broker margin calculator, top rows only - never every row).
// No figure is ever invented: the cell says why there is none. The exact margin is always fetched by the ticket.
export function marginCell(capital) {
  const st = capital?.status;
  if (capital?.capital_required != null && (st === "OK" || st === "STALE")) return { value: capital.capital_required, stale: st === "STALE" };
  if (st === "DISABLED") return { text: "off", title: "The broker margin calculator is switched off in Scanner Settings → Advanced. The exact Zerodha margin is calculated in the trade ticket." };
  if (st === "FAILED" || st === "BREAKER_OPEN") return { text: "unavailable", title: "Zerodha's margin calculator did not answer for this pair. The ticket asks again." };
  if (st === "NO_PRICE") return { text: "no price", title: "No executable price on a leg, so no margin can be asked." };
  return { text: "in ticket", title: "Only the top rows are priced by Zerodha automatically. Open the ticket (or the row) for this pair's exact margin." };
}
